import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import AccordionV2 from '../../components/accordion-v2';
import { AccordionContainer } from '../../components/accordion-v2/accordionContainer';
import { PageContainer } from '../../components/base/pageContainer/pageContainer';
import CookieDeclaration from '../../components/cookieDeclaration';
import CookieButton from '../../components/cookieHubButton/cookieButton';
import { CustomsForm } from '../../components/customsForm/customsForm';
import DynamicRichText from '../../components/dynamicRichText';
import ErrorBoundary from '../../components/errorBoundary';
import HeroImageCta from '../../components/heroImageCta';
import IconButtonList from '../../components/iconButtonList';
import ImageCta from '../../components/imageCta';
import MacroForm from '../../components/macroForm';
import News from '../../components/news';
import NewsLister from '../../components/newsLister';
import PeopleCards from '../../components/peopleCards/';
import ServiceImageCards from '../../components/serviceImageCards';
import ServicePlanCards from '../../components/servicePlanCards';
import StickyMenu from '../../components/stickyMenu';
import Tabs from '../../components/tabs/tabs';
import TrackShipment from '../../components/trackShipment-v2';
import VerticalLinkCards from '../../components/verticalLinkCards';
import media from '../../constants/media';
import IAppFrameViewModel from '../../interfaces/umbracoNodes/appFrameViewModel';
import {
	IAccordion,
	IAccordionContainer,
	ICookieButton,
	ICustomsForm,
	IHeroImageCta,
	IIconButtonList,
	IImageCta,
	IMacroForm,
	INews,
	IPeopleCards,
	IRichText,
	IServiceImageCards,
	IServicePlanCards,
	IStickyMenu,
	ITabs,
	ITrackShipment,
	IVerticalLinkCards,
} from '../../interfaces/umbracoNodes/contentSlices';
import { IPageViewContentModel } from '../../interfaces/umbracoNodes/pageViewModel';

const PageContainerStyled = styled(PageContainer)`
	margin-top: -102px;

	${media.desktopSmall`
		margin-top: -54px;
	`};
`;

interface Props {
	page: IPageViewContentModel;
	children?: ReactNode;
}

const PageContentSliceMapper: FC<Props> = ({ page, children }) => {
	if (!page.Values.ContentSlices || page.Values.ContentSlices.length === 0) {
		return null;
	}

	return (
		<PageContainerStyled>
			{page.Values.ContentSlices.map((slice, index) => (
				<ErrorBoundary key={index} fallback={<ErrorFallback />}>
					{slice.Type === 'trackShipment' && (
						<TrackShipment trackShipment={slice as ITrackShipment}>{children}</TrackShipment>
					)}
					{slice.Type === 'news' && <News news={slice as INews} />}
					{slice.Type === 'newsLister' && <NewsLister />}
					{slice.Type === 'imageCta' && <ImageCta content={slice as IImageCta} />}
					{slice.Type === 'heroImageCta' && (
						<HeroImageCta content={slice as IHeroImageCta} pageId={page.Id} />
					)}
					{slice.Type === 'macroForm' && <MacroForm content={slice as IMacroForm} />}
					{slice.Type === 'servicePlanCards' && <ServicePlanCards content={slice as IServicePlanCards} />}
					{slice.Type === 'serviceImageCards' && <ServiceImageCards content={slice as IServiceImageCards} />}
					{slice.Type === 'verticalLinkCards' && <VerticalLinkCards content={slice as IVerticalLinkCards} />}
					{slice.Type === 'iconButtonList' && <IconButtonList content={slice as IIconButtonList} />}
					{slice.Type === 'accordionContainer' && (
						<AccordionContainer content={slice as IAccordionContainer} />
					)}
					{slice.Type === 'accordionV2' && <AccordionV2 content={slice as IAccordion} />}
					{slice.Type === 'richTextV2' && <DynamicRichText content={slice as IRichText} />}
					{slice.Type === 'peopleCards' && <PeopleCards content={slice as IPeopleCards} />}
					{slice.Type === 'stickyMenu' && <StickyMenu content={slice as IStickyMenu} />}
					{slice.Type === 'customsForm' && <CustomsForm content={slice as ICustomsForm} />}
					{slice.Type === 'tabs' && <Tabs content={slice as ITabs} />}
					{slice.Type === 'cookieHubButton' && <CookieButton content={slice as ICookieButton} />}
					{slice.Type === 'cookieDeclarationV2' && (
						<CookieDeclaration title={(slice as ICookieButton).Values.Title || ''} />
					)}
				</ErrorBoundary>
			))}
		</PageContainerStyled>
	);
};
const CenteredDiv = styled.div`
	display: grid;
	grid-auto-rows: max-content;
	justify-content: center;
	align-items: center;
	color: ${({ theme }) => theme.colors.primaryLight};
`;

const ErrorFallback = (): React.ReactElement => {
	const { appFrame }: { appFrame: IAppFrameViewModel } = useSelector(
		(state: { pageReducer: { appFrame: IAppFrameViewModel } }) => state.pageReducer
	);
	const { t } = useTranslation('common');

	return (
		<CenteredDiv>
			<p>{appFrame.Values.ComponentErrorText || t('error.error-500-unknown')}</p>
		</CenteredDiv>
	);
};

export default PageContentSliceMapper;
